import { useContext, useState, useEffect } from 'react';

import AppContext, {
    isRouteTrack,
    OBJECT_TYPE_NAVIGATION_TRACK,
    OBJECT_TYPE_NAVIGATION_ALONE,
} from '../context/AppContext';
import MapContext from '../context/MapContext';
import TracksManager, { prepareNavigationTrack, getApproximatePoints } from '../manager/track/TracksManager';
import { createUrlParams } from '../util/Utils';
import { parseCoordinates } from '../menu/analyzer/util/PointsManager';
import {
    ROUTE_POINTS_START,
    ROUTE_POINTS_FINISH,
    ROUTE_POINTS_VIA,
    ROUTE_POINTS_AVOID_ROADS,
} from '../store/geoRouter/profileConstants';
import { useLocation } from 'react-router-dom';

export function RouteService() {
    const ctx = useContext(AppContext);
    const mtx = useContext(MapContext);

    const location = useLocation();

    const pinPoint = mtx.pinPoint;

    const routeObject = ctx.routeObject;
    const routeTrack = routeObject.getTrack();
    const routeTrackFile = ctx.routeTrackFile;

    const setRoutingErrorMsg = ctx.setRoutingErrorMsg;
    const setNavigationRoutingInProgress = ctx.setNavigationRoutingInProgress;

    useEffect(() => {
        const startPoint = routeObject.getOption(ROUTE_POINTS_START);
        const finishPoint = routeObject.getOption(ROUTE_POINTS_FINISH);
        if ((startPoint || finishPoint) && ctx.currentObjectType !== OBJECT_TYPE_NAVIGATION_TRACK) {
            ctx.setCurrentObjectType(OBJECT_TYPE_NAVIGATION_ALONE);
        }
    }, [routeObject.getRouteEffectDeps()]);

    function changeRouteText(processRoute) {
        setNavigationRoutingInProgress(processRoute === true);
    }

    const [routeQueryStringParams, setRouteQueryStringParams] = useState({});
    const [routeQueryStringCleanup, setRouteQueryStringCleanup] = useState(false);

    // set query-string
    useEffect(() => {
        if (routeObject.isReady()) {
            let obj = {};

            const startPoint = routeObject.getOption(ROUTE_POINTS_START);
            const finishPoint = routeObject.getOption(ROUTE_POINTS_FINISH);
            const viaPoints = routeObject.getOption(ROUTE_POINTS_VIA);
            const avoidRoads = routeObject.getOption(ROUTE_POINTS_AVOID_ROADS);

            if (startPoint) {
                obj['start'] = startPoint.lat.toFixed(6) + ',' + startPoint.lng.toFixed(6);
            }
            if (finishPoint) {
                obj['end'] = finishPoint.lat.toFixed(6) + ',' + finishPoint.lng.toFixed(6);
            }
            if (viaPoints?.length > 0) {
                obj['via'] = viaPoints
                    .filter((i) => i != null)
                    .map((i) => i.lat.toFixed(6) + ',' + i.lng.toFixed(6))
                    .join(';');
            }
            if (avoidRoads?.length > 0) {
                obj['avoid'] = avoidRoads.map(({ id }) => id).join(';');
            }

            const qs = new URLSearchParams(location.search);

            if (Object.keys(obj).length > 0 || qs.get('profile')) {
                const { type, profile } = routeObject.getProfile();
                type !== 'osmand' && (obj.type = type);
                obj.profile = profile;

                if (routeObject.isParamsChanged()) {
                    const mode = TracksManager.formatRouteMode({
                        includeFalse: true,
                        profile: profile,
                        params: routeObject.getChangedParams(),
                    });
                    obj['params'] = mode.toString().replaceAll('=', ':'); // pretty-url
                }
            }

            // allow-lonely-pin
            if (pinPoint) {
                obj['pin'] = pinPoint.lat.toFixed(6) + ',' + pinPoint.lng.toFixed(6);
            }

            if (Object.keys(obj).length > 0 || routeQueryStringCleanup) {
                setRouteQueryStringCleanup(true);
                setRouteQueryStringParams(obj);
            }
        }
    }, [routeObject.getRouteEffectDeps(), routeObject.getEffectDeps()]);

    // navigate to query-string
    useEffect(() => {
        if (routeObject.isReady() && (Object.keys(routeQueryStringParams).length > 0 || routeQueryStringCleanup)) {
            if (Object.keys(routeQueryStringParams).length === 0) {
                setRouteQueryStringCleanup(false); // only once
            }
            const pretty = createUrlParams(routeQueryStringParams);
            const pageParams = { ...ctx.pageParams };
            pageParams[OBJECT_TYPE_NAVIGATION_TRACK] = pretty;
            ctx.setPageParams(pageParams);
        }
    }, [routeQueryStringParams]);

    // parse query-string
    useEffect(() => {
        const searchParams = new URLSearchParams(globalThis.location.search);

        let shouldZoomToRoute = false;

        const start = parseCoordinates(searchParams.get('start'));
        if (start) {
            routeObject.setOption(ROUTE_POINTS_START, start);
            shouldZoomToRoute = true;
        }

        const finish = parseCoordinates(searchParams.get('finish') || searchParams.get('end'));
        if (finish) {
            routeObject.setOption(ROUTE_POINTS_FINISH, finish);
            shouldZoomToRoute = true;
        }

        if (searchParams.get('via') || searchParams.get('inter')) {
            const via = searchParams.get('via') || searchParams.get('inter');
            const viaPoints = via.split(';').map(parseCoordinates).filter(Boolean);
            routeObject.setOption(ROUTE_POINTS_VIA, viaPoints);
        }

        if (searchParams.get('avoid')) {
            const avoidRoads = [];
            searchParams
                .get('avoid')
                .split(';')
                .forEach((id) => {
                    avoidRoads.push({ id, name: 'Way ' + Math.trunc(id / 64) });
                });
            routeObject.setOption(ROUTE_POINTS_AVOID_ROADS, avoidRoads);
        }

        if (shouldZoomToRoute && !globalThis.location?.hash) {
            routeObject.setOption('route.map.zoom', true);
        }
    }, []);

    // route-by-gpx
    useEffect(() => {
        if (routeTrackFile && routeObject.isReady()) {
            routeObject.calculateGpxRoute({
                routeTrackFile,
                changeRouteText,
                setRoutingErrorMsg,
            });
        }
    }, [routeObject.getEffectDeps(), routeTrackFile]);

    // route-by-start-finish
    useEffect(() => {
        if (!routeTrackFile && routeObject.isReady() && routeObject.isRouteReadyToCalc()) {
            routeObject.calculateRoute({
                changeRouteText,
                setRoutingErrorMsg,
            });
        } else if (!routeTrackFile) {
            setNavigationRoutingInProgress(false);
        }
    }, [routeObject.getEffectDeps(), routeObject.getRouteEffectDeps(), routeTrackFile]);

    // routeTrack: approximate segments
    // routeTrack: refresh selectedGpxFile
    // routeTrack: auto-srtm will be applied by GeneralInfo
    useEffect(() => {
        async function setNavigationTrack() {
            if (routeTrack) {
                const { type, profile } = routeObject.getGeoProfile();
                const track = prepareNavigationTrack(routeTrack);

                // auto-approximate after OSRM (limited)
                const props = routeObject.getRouteProps();
                if (
                    type === 'osrm' &&
                    (routeObject.getOption('route.map.forceApproximation') ||
                        props?.overall?.distance <= process.env.REACT_APP_MAX_APPROXIMATE_KM * 1000)
                ) {
                    // approximate each segment separately
                    for (let i = 0; i < track.points.length; i++) {
                        const geometry = track.points[i].geometry;
                        if (geometry.length > 0) {
                            track.points[i].geometry = await getApproximatePoints({ points: geometry, profile });
                        }
                    }
                }

                // do not open InfoBlock until openInfoBlock activated (NavigationMenu)
                if (isRouteTrack(ctx)) {
                    // re-open InfoBlock
                    ctx.setUpdateInfoBlock(true);
                    ctx.setSelectedGpxFile(track);
                    ctx.setCurrentObjectType(OBJECT_TYPE_NAVIGATION_TRACK);
                } else {
                    ctx.setCurrentObjectType(OBJECT_TYPE_NAVIGATION_ALONE); // invoke MainMenu.js effect
                }
            } else {
                if (isRouteTrack(ctx)) {
                    ctx.setSelectedGpxFile({}); // close-route-track
                }
            }
        }
        setNavigationTrack();
    }, [routeTrack]);

    return null;
}
